import React, { useEffect } from "react";
import { Button, Dialog } from "@/components/ui";
import { FormProvider, useForm } from "react-hook-form";
import { SuperPageEligibilityValues, SuperPageServices } from "@/services/bedrock/SuperPageServices";
import { SuperPageEligibilityForm } from "./SuperPageEligibilityForm";
import { toast } from "sonner";
import { useClearVideoPageCache } from "@/utils";

interface Props {
  open: boolean;
  superPageId: number | undefined;
  onOpenChange: (open: boolean) => void;
}

const SuperPageEligibilityDialog: React.FC<Props> = ({ open, superPageId, onOpenChange }) => {
  const methods = useForm<SuperPageEligibilityValues>({
    defaultValues: {
      id: null,
      eligibilityCriterions: [],
    },
  });
  const {
    handleSubmit,
    formState: { dirtyFields, isSubmitting, isDirty },
  } = methods;

  useEffect(() => {
    if (!open || !superPageId) return;

    SuperPageServices.getSuperPage(superPageId, ["eligibilityCriterions"])
      .then(superPage => {
        methods.reset({
          id: superPageId,
          eligibilityCriterions: superPage.eligibilityCriterions,
        });
      })
      .catch(error => {
        console.log(error);
        toast.error("Error getting Super Page");
        onOpenChange(false);
      });
  }, [open, superPageId]);

  const { clearVideoPageCache } = useClearVideoPageCache();

  const handleSaveSubmit = (form: SuperPageEligibilityValues) => {
    if (!superPageId) return;

    const changes = Object.fromEntries(
      Object.entries(form).filter(([key, _]) => dirtyFields[key as keyof SuperPageEligibilityValues] === true)
    );
    return SuperPageServices.updateSuperPage(superPageId, changes)
      .then(() => {
        toast.success("Super Page updated successfully");
        clearVideoPageCache({ superPages: true });
      })
      .then(() => onOpenChange(false))
      .catch(error => {
        console.log(error);
        toast.error("Error updating Super Page");
      });
  };

  return (
    <Dialog open={open} size="xl" className="top-16 translate-y-0" onOpenChange={onOpenChange}>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(handleSaveSubmit)} className="flex flex-col">
          <Dialog.Header>
            <Dialog.Title>Edit Eligibility</Dialog.Title>
          </Dialog.Header>
          <Dialog.Content>
            <SuperPageEligibilityForm />
          </Dialog.Content>
          <Dialog.Footer>
            {superPageId && (
              <div className="flex justify-end items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  children="Cancel"
                  disabled={isSubmitting}
                  onClick={() => onOpenChange(false)}
                />
                <Button type="submit" children="Save" disabled={!isDirty} loading={isSubmitting} />
              </div>
            )}
          </Dialog.Footer>
        </form>
      </FormProvider>
    </Dialog>
  );
};

export { SuperPageEligibilityDialog };
